import { graphql } from '@/__generated__';
import { useQuery } from '@apollo/client';
import { omit, uniqBy } from 'lodash-es';
import useTranslation from 'next-translate/useTranslation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ModalDropdown as Dropdown } from '@/components/common/Modal/components';
import { DropdownProps } from '@/components/common/Modal/components/ModalDropdown';

const GetSchoolList = graphql(`
  query Schools(
    $first: Int
    $after: Cursor
    $last: Int
    $filterBy: SchoolFilter
    $filterBy2: SchoolFilter
  ) {
    schools(first: $first, after: $after, last: $last, filterBy: $filterBy) {
      edges {
        node {
          id
          domain {
            id
          }
          province {
            id
            name
            provinceId
          }
          name
          schoolId
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        startCursor
        hasPreviousPage
      }
    }
    schoolsInit: schools(first: 1, filterBy: $filterBy2) {
      edges {
        node {
          id
          domain {
            id
          }
          province {
            id
            name
            provinceId
          }
          name
          schoolId
        }
      }
    }
  }
`);

interface RankingSchoolProps extends Omit<DropdownProps, 'items'> {
  city?: string | null;
  setProvince?: (value: string, schoolId: string) => void;
  initSchoolId?: string;
  onChange?: (value: string | undefined, id: string | undefined) => void;
}

function RankingSchool({
  onChange,
  city,
  setProvince,
  initSchoolId,
  ...props
}: RankingSchoolProps) {
  const [inputValue, setInputValue] = useState('');
  const [keySearch, setKeySearch] = useState('');

  // const [data, setData] = useState<{ title: string; value: string; provinceId: string }[]>([]);

  // const [itemSelectByPass, setItemSelectByPass] = useState<
  //   SchoolsQuery['schools']['edges'][number] | undefined
  // >();

  const { t } = useTranslation('common');

  const {
    data: dataSchools,
    fetchMore,
    loading,
  } = useQuery(GetSchoolList, {
    variables: {
      first: 25,
      filterBy: {
        provinceId: city ? [city] : undefined,
        nameQ: keySearch ? [keySearch?.trim()] : undefined,
      },
      filterBy2: {
        id: initSchoolId ? [initSchoolId] : undefined,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const dataSchoolMap = useMemo(() => {
    const dataInit =
      dataSchools?.schoolsInit?.edges?.map((el) => {
        return {
          title: el.node.name,
          provinceId: el.node.province?.provinceId,
          value: el.node.schoolId?.toString(),
        };
      }) || [];

    const dataList =
      dataSchools?.schools?.edges?.map((school) => {
        return {
          title: school.node.name,
          provinceId: school.node.province?.provinceId,
          value: school.node.schoolId?.toString(),
        };
      }) || [];
    return [...dataInit, ...dataList];
  }, [dataSchools?.schools?.edges, dataSchools?.schoolsInit?.edges]);

  const handlerOnEnd = useCallback(() => {
    if (dataSchools?.schools?.pageInfo?.hasNextPage) {
      void fetchMore({
        variables: {
          first: 10,
          after: dataSchools?.schools?.pageInfo?.endCursor,
        },
      });
    }
  }, [dataSchools?.schools, fetchMore]);

  // handler search
  useEffect(() => {
    const timer = setTimeout(() => {
      setKeySearch(inputValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const items = useMemo(() => {
    return uniqBy(dataSchoolMap, 'value');
  }, [dataSchoolMap]);

  const handleChange = useCallback(
    (value: string | undefined, id: string | undefined) => {
      if (onChange) {
        onChange(value, id);
        if (setProvince) {
          const itemFind = items.find((item) => item.value === value);
          const provinceId = itemFind?.provinceId;
          if (provinceId && value && provinceId !== city) {
            setProvince(provinceId, value);
          }
        }
      }
    },
    [city, items, onChange, setProvince],
  );

  return (
    <Dropdown
      items={items}
      hasSearchable
      focusOnOpen
      onSelect={handleChange}
      {...omit(props, 'items')}
      queryPlaceholder={t('label.search_with_test_name')}
      placeholder={t('label.school_placeholder')}
      canClearSelected
      inputValue={inputValue}
      onInputChange={(newValue) => {
        setInputValue(newValue);
      }}
      isCanLoadMore={!loading}
      onScrollEnd={handlerOnEnd}
      loadingSearch={loading && !!keySearch}
    />
  );
}

export default RankingSchool;
